<template>
    <div class="map__container">
        <div class="map__canvas" id="map__canvas"></div>
        <div class="map-filter" :class="inIframeAdditionalClass">
            <div class="map-filter__input-autocomplete">
                <DsInput
                    id="placeInput"
                    v-model="placeValue"
                    class="map-filter__place-input"
                    :icon-right="placeInputIcon"
                    :right-icon-click-fn="placeInputIconClick"
                    @keyup.enter="filterPlaces"
                    placeholder="Zadejte místo"
                ></DsInput>
                <div id="autocomplete"></div>
            </div>
            <DsDropdown
                id="draw-date"
                :options="servicesOptions"
                v-model="selectedServiceId"
                class="map-filter__services-dropdown"
            />
            <DsButton @click="filterPlaces">Hledat</DsButton>
        </div>
        <div class="detail" :class="inIframeAdditionalClass" id="detail">
            <div class="detail__content">
                <h3 id="place-name" class="detail__name"></h3>
                <div id="place-address" class="detail__address"></div>
                <hr class="ds-divider" />
                <table id="opening-hours" class="detail__hours"></table>
                <h4 class="detail__services-header">Dostupné služby</h4>
                <ul class="detail__services-list" id="service-list"></ul>
            </div>
        </div>
        <ul class="legend">
            <li class="legend__item legend__item--blue">Prodejní místa (Terminály)</li>
            <li class="legend__item legend__item--orange">Sázení na pokladnách</li>
        </ul>
    </div>
    <div id="map-consent" class="map-consent__background">
        <div class="map-consent__info"></div>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch, computed, onMounted } from "vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import DsInput from "@sazka/design-system/src/forms/components/ds-input.vue";
import DsDropdown from "@sazka/design-system/src/forms/components/ds-dropdown.vue";
import { DsDropdownItemModel } from "@sazka/design-system/src/forms/components/ds-dropdown/ds-dropdown-models";
import MarkerClusterer from "@googlemaps/markerclustererplus";
import { getClusterOptions } from "./cluster-options";
import { getIcon } from "./icons";
import { Loader } from "@sazka/design-system/src/loader/loader";
import { getMapOptions } from "./map-options";
import { handleConsentOverlay } from "@sazka/utils/gdpr/consent-overlay";
import { eventBus, GlobalEvents } from "@sazka/utils/global-events";
import { UrlUtils } from "@sazka/utils/url";

const props = defineProps<{
    jsonServicesOptions: string;
}>();

const servicesOptions: Array<DsDropdownItemModel> = JSON.parse(props.jsonServicesOptions);

const placeValue = ref("");
const selectedServiceId = ref<number>(0);

const filterPlaces = async () => {
    filter(placeValue.value, selectedServiceId.value);
    hideAutocomplete();
};

const placeInputIconClick = () => {
    if (placeValue.value?.length) {
        placeValue.value = "";
        filterPlaces();
    }
};

const placeInputIcon = computed(() => (placeValue.value?.length ? "cross" : "search"));
const inIframeAdditionalClass = computed(() => (UrlUtils.getQueryString("iframe") ? "map--in-iframe" : ""));

watch(placeValue, () => {
    if (placeValue.value.length >= 4) {
        getAutocomplete(placeValue.value);
    } else {
        hideAutocomplete();
    }
});

(window as any).initMap = function () {
    loadMap();
};

async function showConsentOverlay() {
    const consentElement = document.getElementById("map-consent");
    const overlayShown = await handleConsentOverlay(".map-consent__info", null, "map");

    if (overlayShown) {
        return;
    } else {
        consentElement.style.display = "none";
        const scriptElement = document.createElement("script");
        scriptElement.type = "text/javascript";
        scriptElement.async = true;
        scriptElement.src =
            "//maps.googleapis.com/maps/api/js?key=AIzaSyCKIamHEX4rIbArrnZjmtK5jN33JxBIk08&callback=initMap&language=cs";
        document.body.appendChild(scriptElement);
    }
}

interface MarkerObject {
    x: string;
    y: string;
    i: string;
    /// 0 - shop, 1 - terminal
    t: 0 | 1;
}

declare const google: any;

const elementId = "map__canvas";
const markerClusterTerminalOptions = getClusterOptions("terminal");
const markerClusterShopOptions = getClusterOptions("store");

let markerClustererTerminal: any;
let markerClustererShop: any;
let markerStore: Array<MarkerObject>;
let markers: Array<any> = [];
let map: any;
let iconOrange: any;
let iconBlue: any;
let czechBounds: any;
let lastBounds = null;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let tilesLoadedListener = null;
let previousFilterState = { text: "", serviceId: 0 };

function loadMap() {
    czechBounds = new google.maps.LatLngBounds();
    czechBounds.extend({ lat: 51.051387, lng: 14.311679 });
    czechBounds.extend({ lat: 49.545278, lng: 18.850935 });
    czechBounds.extend({ lat: 48.555004, lng: 14.335386 });
    czechBounds.extend({ lat: 50.272459, lng: 12.100383 });

    const options = getMapOptions();

    iconOrange = getIcon("store");
    iconBlue = getIcon("terminal");

    markerStore = (window as any).mapTerminals;

    const mapElem = document.getElementById(elementId);
    map = new google.maps.Map(mapElem, options);
    reloadMarkers();

    mapElem.onclick = function () {
        const detailElem = document.getElementById("detail");
        if (detailElem.classList.contains("active")) {
            document.getElementById("detail").classList.toggle("active");
        }
    };

    document.getElementById("autocomplete").addEventListener(
        "click",
        (event) => {
            let matched = checkMatch(event.target);
            if (matched) {
                autocompleteDetailClick(event.target);
                return;
            }

            // check parent elem if clicked on highlighted word
            matched = checkMatch((event.target as any).parentElement);
            if (matched) {
                autocompleteDetailClick((event.target as any).parentElement);
            }
        },
        false
    );

    const filterObject = { text: placeValue.value, serviceId: selectedServiceId.value };
    previousFilterState = filterObject;
}

function checkMatch(elem: EventTarget): boolean {
    const c = ".autocomplete__item";
    const e = elem as any;

    return (
        (e.matches && e.matches(c)) ||
        (e.matchesSelector && e.matchesSelector(c)) ||
        (e.webkitMatchesSelector && e.webkitMatchesSelector(c)) ||
        (e.webkitMatches && e.webkitMatches(c)) ||
        (e.msMatchesSelector && e.msMatchesSelector(c))
    );
}

function reloadMarkers() {
    lastBounds = new google.maps.LatLngBounds();

    if (markerStore.length) {
        for (let i = 0; i < markerStore.length; i++) {
            (function (marker) {
                const lat = parseFloat(marker.x);
                const lng = parseFloat(marker.y);
                const location = new google.maps.LatLng(lat, lng);
                if (lat > 1 && lng > 1) {
                    const mapMarker = new google.maps.Marker({
                        position: location,
                        icon: marker.t == 0 ? iconBlue : iconOrange,
                        optimized: false,
                    });
                    mapMarker.storeId = marker.i;
                    mapMarker.sazkaType = marker.t;
                    lastBounds.extend(location);

                    google.maps.event.addListener(mapMarker, "click", () => {
                        getDetail(marker.i as any);
                    });
                    markers.push(mapMarker);
                }
            })(markerStore[i]);
        }
    } else {
        lastBounds = czechBounds;
    }

    map.fitBounds(lastBounds);

    reloadClusters();
    tilesLoadedListener = google.maps.event.addListener(map, "tilesloaded", () => {
        Loader.hide();
    });
}

async function reloadClusters() {
    if (!markerClustererShop) {
        markerClustererShop = new MarkerClusterer(
            map,
            markers.filter((x) => x.sazkaType == 1),
            markerClusterShopOptions
        );
    } else {
        markerClustererShop.clearMarkers();
        markerClustererShop.addMarkers(markers.filter((x) => x.sazkaType === 1));
    }

    if (!markerClustererTerminal) {
        markerClustererTerminal = new MarkerClusterer(
            map,
            markers.filter((x) => x.sazkaType == 0),
            markerClusterTerminalOptions
        );
    } else {
        markerClustererTerminal.clearMarkers();
        markerClustererTerminal.addMarkers(markers.filter((x) => x.sazkaType === 0));
    }
}

function hideMarkers() {
    for (let i = 0; i < markers.length; i++) {
        markers[i].setMap(null);
    }
}

function deleteMarkers() {
    markerStore = [];
}

function filter(text: string, type: number) {
    document.getElementById("detail").classList.remove("active");
    const filterObject = { text: text, serviceId: type };
    // check filter is same as previous
    if (
        previousFilterState &&
        previousFilterState.text == filterObject.text &&
        previousFilterState.serviceId == filterObject.serviceId
    ) {
        map.fitBounds(lastBounds);
        return;
    }
    previousFilterState = filterObject;
    sendApiRequest("/api/draw-info/terminals/filter", "POST", (data) => filterResponse(data), filterObject);
}

function filterResponse(data): void {
    if (typeof data === "string") {
        data = JSON.parse(data);
    }
    hideMarkers();
    deleteMarkers();
    markers = [];
    markerStore = data;
    reloadMarkers();
    const detail = document.getElementById("detail");
    detail.classList.remove("active", "ready");
}

function getDetail(id: number) {
    sendApiRequest(`/api/draw-info/terminals/${id}`, "GET", (data) => detailResponse(data));
}

function detailResponse(data): void {
    if (typeof data === "string") {
        data = JSON.parse(data);
    }
    map.setZoom(17);
    map.panTo({ lat: Number(data.lat), lng: Number(data.lng) });
    document.getElementById("place-name").innerHTML = data.name;
    document.getElementById("place-address").innerHTML = `${data.address1}<br/>${data.address2}`;
    document.getElementById("opening-hours").innerHTML = `
            <tr${data.monday ? "" : ' class="closed" '}><td>PO</td><td>${data.monday || "Zavřeno"}</td></tr>
            <tr${data.tuesday ? "" : ' class="closed" '}><td>ÚT</td><td>${data.tuesday || "Zavřeno"}</td></tr>
            <tr${data.wednesday ? "" : ' class="closed" '}><td>ST</td><td>${data.wednesday || "Zavřeno"}</td></tr>
            <tr${data.thursday ? "" : ' class="closed" '}><td>ČT</td><td>${data.thursday || "Zavřeno"}</td></tr>
            <tr${data.friday ? "" : ' class="closed" '}><td>PÁ</td><td>${data.friday || "Zavřeno"}</td></tr>
            <tr${data.saturday ? "" : ' class="closed" '}><td>SO</td><td>${data.saturday || "Zavřeno"}</td></tr>
            <tr${data.sunday ? "" : ' class="closed" '}><td>NE</td><td>${data.sunday || "Zavřeno"}</td></tr>`;
    document.getElementById("service-list").innerHTML = data.services.map((x) => `<li>${x}</li>`).join("");
    const detail = document.getElementById("detail");
    detail.classList.add("active", "ready");
}

function sendApiRequest(
    url: string,
    type: "GET" | "POST",
    callback: (data) => void,
    data: any = null,
    showLoader = true
) {
    if (showLoader) {
        Loader.show();
    }
    const req = new XMLHttpRequest();
    req.open(type, url);
    req.setRequestHeader("Content-Type", "application/json");
    req.setRequestHeader("Accept", "application/json");
    req.responseType = "json";
    req.onload = (ev: ProgressEvent) => apiRequestHandler(ev, callback);
    req.send(data ? JSON.stringify(data) : null);
}

function apiRequestHandler(response: ProgressEvent, callback: (data) => void) {
    if ((response.target as any).status == 200) {
        callback((response.target as any).response);
    } else {
        console.error("API error");
    }
    Loader.hide();
}

//#region Autocomplete
function hideAutocomplete() {
    const autocomplete = document.getElementById("autocomplete");
    autocomplete.innerHTML = "";
    // IE 11 fix
    autocomplete.style.display = "none";
}

function getAutocomplete(text) {
    const filterObject = { text: text, serviceId: 0 };
    sendApiRequest(
        "/api/draw-info/terminals/autocomplete",
        "POST",
        (data) => autocompleteResponse(data, text),
        filterObject,
        false
    );
}

function autocompleteResponse(data, text) {
    if (typeof data === "string") {
        data = JSON.parse(data);
    }
    const regex = new RegExp(text, "gi");
    if (data?.length) {
        const items = data.map(
            (x) =>
                `<span class='autocomplete__item' data-id='${x.i}'>${x.n.replace(regex, replacement)} ${x.s.replace(
                    regex,
                    replacement
                )} ${x.c.replace(regex, replacement)}</span>`
        );
        const autocomplete = document.getElementById("autocomplete");
        autocomplete.innerHTML = items.join("");
        autocomplete.style.display = "block";
    } else {
        hideAutocomplete();
    }
}

function autocompleteDetailClick(item) {
    const id = (item as HTMLElement).getAttribute("data-id");
    getDetail(Number(id));
    hideAutocomplete();
}

function replacement(text) {
    return `<span class="match">${text}</span>`;
}
//#endregion

onMounted(async () => {
    await showConsentOverlay();
    eventBus.on(GlobalEvents.ConsentsChanged, async () => await showConsentOverlay());
});
</script>
